import * as React from "react";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";

import { TrashIcon } from "@/components/ui/trash";
import { Input as ShadcnInput } from "@/components/ui/input";
import type {
  PolicyTreeV2,
  PolicyTreeV2Rule,
  TradePolicyCatalogField,
  TradePolicyGroupNode,
} from "../../../types/tradePolicy";
import { humanizeManagementAction } from "../../../utils/managementActionLabels";
import {
  distanceAtrFromRule,
  exitPlacementRuleDisplayTitle,
  updatePolicyRuleAtIndex,
  updateRuleDistanceAtr,
} from "../../../utils/exitPlacementPolicy";
import { PolicyGraphCanvas } from "./PolicyGraphCanvas";
import { PolicyTreeNodesAccordion } from "./PolicyTreeAccordion";
import {
  numericGlossaryHelperForPath,
  type PolicyRuleFieldExplanation,
} from "./policyFieldExplanation";
import {
  countRuleLeaves,
  type EditorNode,
  type EditorRuleNode,
} from "./policyTreeUtils";

const noop = (): void => undefined;
const noopId = (_id: string): void => undefined;
const noopRulePatch = (_id: string, _patch: Partial<EditorRuleNode>): void => undefined;
const noopDrag = (_id: string, _e: React.DragEvent<HTMLElement>): void => undefined;
const noopGroupDrag = (_id: string, _e: React.DragEvent<HTMLDivElement>): void => undefined;
const TRADE_LOG_LOSS_RED = "#E00101";

type PolicyViewMode = "builder" | "graph";

function ruleSubtitle(rule: PolicyTreeV2Rule): string {
  return (rule.actions ?? [])
    .map((action) => humanizeManagementAction(action))
    .filter(Boolean)
    .join(" · ");
}

function AtrDistanceField(props: {
  id: string;
  label: string;
  value: number | null;
  onChange: (next: number | null) => void;
  readOnly?: boolean;
}): React.ReactElement {
  const subtleText = useColorModeValue("gray.500", "gray.400");
  const [draft, setDraft] = React.useState(() =>
    props.value != null && Number.isFinite(props.value) ? String(props.value) : ""
  );

  React.useEffect(() => {
    setDraft(props.value != null && Number.isFinite(props.value) ? String(props.value) : "");
  }, [props.value]);

  return (
    <FormControl minW="8.5rem" flex="1">
      <FormLabel htmlFor={props.id} fontSize="xs" color={subtleText} mb={1} fontFamily="var(--font-primary)">
        {props.label}
      </FormLabel>
      <ShadcnInput
        id={props.id}
        type="number"
        inputMode="decimal"
        min={0.01}
        step={0.01}
        value={draft}
        readOnly={props.readOnly}
        disabled={props.readOnly}
        onChange={(e) => {
          if (props.readOnly) return;
          const raw = e.target.value;
          setDraft(raw);
          const trimmed = raw.trim();
          if (!trimmed) {
            props.onChange(null);
            return;
          }
          const n = Number(trimmed);
          if (Number.isFinite(n) && n > 0) props.onChange(n);
        }}
        onBlur={() => {
          if (props.readOnly) return;
          const trimmed = draft.trim();
          if (!trimmed) return;
          const n = Number(trimmed);
          if (Number.isFinite(n) && n > 0) props.onChange(n);
        }}
        className="h-8 font-[family-name:var(--font-ibm-plex)] text-sm tabular-nums"
      />
    </FormControl>
  );
}

function ExitPlacementRuleEditor(props: {
  rule: PolicyTreeV2Rule;
  ruleIndex: number;
  displayNodes: EditorNode[];
  catalogFields: TradePolicyCatalogField[];
  catalogFieldsForPicker: TradePolicyCatalogField[];
  fieldsByPath: Map<string, TradePolicyCatalogField>;
  fieldExplanationFor: (field: TradePolicyCatalogField) => PolicyRuleFieldExplanation;
  hideGroupAction: boolean;
  ruleLabelsById?: Map<string, string>;
  policyViewMode: PolicyViewMode;
  collapsedGroupIds: Set<string>;
  setCollapsedGroupIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  borderColor: string;
  textColor: string;
  subtleText: string;
  onPolicyChange?: (policy: PolicyTreeV2) => void;
  policy: PolicyTreeV2;
  onRemoveRule?: (ruleId: string) => void;
  readOnly?: boolean;
}): React.ReactElement {
  const ruleLeaves = React.useMemo(() => {
    let total = 0;
    for (const node of props.displayNodes) total += countRuleLeaves(node);
    return total;
  }, [props.displayNodes]);
  const slAtr = distanceAtrFromRule(props.rule, "sl_distance_override");
  const tpAtr = distanceAtrFromRule(props.rule, "tp_distance_override");
  const title = exitPlacementRuleDisplayTitle(props.rule, props.ruleIndex, props.ruleLabelsById);
  const subtitle = ruleSubtitle(props.rule);
  const readOnly = Boolean(props.readOnly);
  const showSl = !readOnly || slAtr != null;
  const showTp = !readOnly || tpAtr != null;

  const patchRule = (patch: (rule: PolicyTreeV2Rule) => PolicyTreeV2Rule) => {
    if (readOnly || !props.onPolicyChange) return;
    props.onPolicyChange(updatePolicyRuleAtIndex(props.policy, props.ruleIndex, patch));
  };

  return (
    <Box borderWidth="1px" borderColor={props.borderColor} borderRadius="12px" p={4}>
      <Flex align="flex-start" justify="space-between" gap={3} mb={3}>
        <VStack align="stretch" spacing={1} flex="1" minW={0}>
          <Text fontSize="sm" fontWeight="semibold" color="#38BBD1" fontFamily="'Urbanist', sans-serif">
            {title}
          </Text>
          {subtitle ? (
            <Text fontSize="xs" color={props.subtleText} fontFamily="'IBM Plex Sans', sans-serif">
              {subtitle}
            </Text>
          ) : null}
        </VStack>
        {!readOnly && props.onRemoveRule && props.rule.id?.trim() ? (
          <IconButton
            aria-label="Remove rule from policy tree"
            icon={<TrashIcon size={18} aria-hidden />}
            size="sm"
            variant="ghost"
            color={TRADE_LOG_LOSS_RED}
            flexShrink={0}
            onClick={() => props.onRemoveRule?.(props.rule.id!.trim())}
          />
        ) : null}
      </Flex>

      <Text fontSize="xs" fontWeight={500} color="#38BBD1" mb={2} fontFamily="var(--font-primary)">
        WHEN
      </Text>
      {props.displayNodes.length > 0 ? (
        props.policyViewMode === "builder" ? (
          <PolicyTreeNodesAccordion
            nodes={props.displayNodes}
            catalogFields={props.catalogFields}
            catalogFieldsForPicker={props.catalogFieldsForPicker}
            fieldsByPath={props.fieldsByPath}
            ruleLeaves={ruleLeaves}
            collapsedNodeIds={props.collapsedGroupIds}
            setCollapsedNodeIds={props.setCollapsedGroupIds}
            canRemoveGroup={false}
            readOnly
            hideGroupAction={props.hideGroupAction}
            onUpdateRule={noopRulePatch}
            onRulePathChange={noopId}
            onRuleOpChange={noopId}
            onGroupOpChange={noopId as (id: string, op: TradePolicyGroupNode["op"]) => void}
            onGroupLabelChange={noopId}
            onGoToSourcePick={noopId}
            onRemove={noopId}
            onAddRule={noopId}
            draggedRuleId={null}
            onRuleDragStart={noop}
            onRuleDragEnd={noop}
            onGroupDragOver={noopGroupDrag}
            onGroupDropRule={noopGroupDrag}
            numericHintForPath={numericGlossaryHelperForPath}
            fieldExplanationFor={props.fieldExplanationFor}
            defaultGroupAction="veto"
            onGroupActionChange={noop}
          />
        ) : (
          <PolicyGraphCanvas
            roots={props.displayNodes}
            catalogFields={props.catalogFields}
            catalogFieldsForPicker={props.catalogFieldsForPicker}
            fieldsByPath={props.fieldsByPath}
            fieldExplanationFor={props.fieldExplanationFor}
            readOnly
            hideGroupAction={props.hideGroupAction}
            onAddGroupNode={noop}
            onConnectRuleToGroup={noop}
            onDetachConnection={noop}
            onRemoveNode={noopId}
            onUpdateRule={noopRulePatch}
            onRulePathChange={noopId}
            onRuleOpChange={noopId}
            onGroupOpChange={noopId as (id: string, op: TradePolicyGroupNode["op"]) => void}
            onGroupLabelChange={noopId}
            onAddRuleToGroup={noopId}
            defaultGroupAction="veto"
            onGroupActionChange={noop}
          />
        )
      ) : (
        <Text fontSize="sm" color={props.subtleText}>
          No condition defined.
        </Text>
      )}

      <Text fontSize="xs" fontWeight={500} color="#38BBD1" mt={4} mb={2} fontFamily="var(--font-primary)">
        THEN — SL/TP distance (ATR)
      </Text>
      {showSl || showTp ? (
        <Flex gap={3} flexWrap="wrap" align="flex-end">
          {showSl ? (
            <AtrDistanceField
              id={`exit-placement-sl-atr-${props.ruleIndex}`}
              label="Stop distance (ATR)"
              value={slAtr}
              readOnly={readOnly}
              onChange={(next) => {
                if (next == null) return;
                patchRule((rule) => updateRuleDistanceAtr(rule, "sl_distance_override", next));
              }}
            />
          ) : null}
          {showTp ? (
            <AtrDistanceField
              id={`exit-placement-tp-atr-${props.ruleIndex}`}
              label="Target distance (ATR)"
              value={tpAtr}
              readOnly={readOnly}
              onChange={(next) => {
                if (next == null) return;
                patchRule((rule) => updateRuleDistanceAtr(rule, "tp_distance_override", next));
              }}
            />
          ) : null}
        </Flex>
      ) : (
        <Text fontSize="sm" color={props.subtleText} fontFamily="'IBM Plex Sans', sans-serif">
          No SL/TP actions on this rule.
        </Text>
      )}
    </Box>
  );
}

export type ExitPlacementPolicyTreeEditorProps = {
  policy: PolicyTreeV2;
  /** Pre-parsed WHEN nodes per rule — must match ids used for Hide all / Show all. */
  ruleDisplayNodes: EditorNode[][];
  catalogFields: TradePolicyCatalogField[];
  catalogFieldsForPicker: TradePolicyCatalogField[];
  fieldsByPath: Map<string, TradePolicyCatalogField>;
  fieldExplanationFor: (field: TradePolicyCatalogField) => PolicyRuleFieldExplanation;
  policyViewMode: PolicyViewMode;
  collapsedGroupIds: Set<string>;
  setCollapsedGroupIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  hideGroupAction?: boolean;
  ruleLabelsById?: Map<string, string>;
  onPolicyChange?: (policy: PolicyTreeV2) => void;
  onRemoveRule?: (ruleId: string) => void;
  /** View-only: disable ATR edits and hide remove. */
  readOnly?: boolean;
};

export function ExitPlacementPolicyTreeEditor(props: ExitPlacementPolicyTreeEditorProps): React.ReactElement {
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");
  const textColor = useColorModeValue("gray.800", "white");
  const subtleText = useColorModeValue("gray.500", "gray.400");
  const rules = props.policy.rules ?? [];

  if (rules.length === 0) {
    return (
      <Text fontSize="sm" color={subtleText} fontFamily="'IBM Plex Sans', sans-serif">
        {props.readOnly
          ? "No exit placement rules in this policy."
          : (
              <>
                Policy tree is empty. Select picks and use <strong>Insert into policy tree</strong>.
              </>
            )}
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      {rules.map((rule, ruleIndex) => (
        <ExitPlacementRuleEditor
          key={rule.id?.trim() || `exit-placement-rule-${ruleIndex}`}
          rule={rule}
          ruleIndex={ruleIndex}
          displayNodes={props.ruleDisplayNodes[ruleIndex] ?? []}
          policy={props.policy}
          catalogFields={props.catalogFields}
          catalogFieldsForPicker={props.catalogFieldsForPicker}
          fieldsByPath={props.fieldsByPath}
          fieldExplanationFor={props.fieldExplanationFor}
          hideGroupAction={props.hideGroupAction ?? true}
          ruleLabelsById={props.ruleLabelsById}
          policyViewMode={props.policyViewMode}
          collapsedGroupIds={props.collapsedGroupIds}
          setCollapsedGroupIds={props.setCollapsedGroupIds}
          borderColor={borderColor}
          textColor={textColor}
          subtleText={subtleText}
          onPolicyChange={props.onPolicyChange}
          onRemoveRule={props.onRemoveRule}
          readOnly={props.readOnly}
        />
      ))}
    </VStack>
  );
}
